import { useState, useEffect } from "react"
import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import PhotoLightbox from "@/components/PhotoLightbox"
import { ChevronLeft } from "lucide-react"
import { HeroFilmSlides } from "@/components/gisela/HeroBand"
import { headingSizeClass } from "@/lib/heading-size"
import { sectionHidden } from "@/lib/section-visibility"
import { BrandMotif } from "@/components/ui/brand-motif"
import type { PageProps } from "@/types/inertia"

interface Photo {
  id: number
  url: string
  full_url: string
  alt_text: string
}

interface Category {
  key: string
  label: string
  photos: Photo[]
}

interface HeroPhoto {
  id: number
  url: string | null
  alt_text: string | null
}

interface Props {
  site_name: string
  back_href: string
  back_label: string
  /** The gallery's own hero band, cut to its display settings. */
  hero?: { slides: HeroPhoto[]; interval_seconds: number; film?: string | null; heading_size?: string | null }
  content?: { hero: { eyebrow?: string; heading?: string; subheading?: string; _hidden?: boolean } }
  categories: Category[]
}

export default function SiteGallery({
  site_name,
  back_href,
  back_label,
  hero = { slides: [], interval_seconds: 6 },
  content = { hero: {} },
  categories
}: Props) {
  const { translations } = usePage<PageProps>().props
  const t = translations.sanctuary_pages.gallery
  const allPhotos = categories.flatMap((c) => c.photos)
  const heroSlides = hero.slides.filter((s): s is HeroPhoto & { url: string } => !!s.url)
  const [lightbox, setLightbox] = useState<number | null>(null)

  // A room card links here with #room_<id>, and lands as though the visitor had
  // already been in the gallery and clicked that room's first photo: the
  // lightbox opens on it, and its arrows run through every photo on the page,
  // not just that room's. The room is where they start, not where they are
  // kept. Behind the lightbox we also scroll to the room's own section, so
  // closing it leaves them in the right place rather than at the top.
  useEffect(() => {
    const key = window.location.hash.slice(1)
    if (!key) return

    const first = categories.find((c) => c.key === key)?.photos[0]
    if (first) {
      const index = allPhotos.findIndex((p) => p.id === first.id)
      if (index >= 0) setLightbox(index)
    }

    const jump = () => document.getElementById(key)?.scrollIntoView({ block: "start" })
    jump()
    // The photos above the target have no dimensions yet, so that first jump
    // lands short once they load and push the page down. Land on it again when
    // they are in.
    if (document.readyState === "complete") return
    window.addEventListener("load", jump, { once: true })
    return () => window.removeEventListener("load", jump)
  }, [])

  const heading = content.hero.heading || t.site_heading.replace("%{name}", site_name)
  const photoCount = (allPhotos.length === 1 ? t.photo_count_one : t.photo_count_other).replace(
    "%{count}",
    String(allPhotos.length)
  )

  const pageTitle = `${site_name} — Photo Gallery | Sanctuary Therapy Centre`
  const pageDesc  = `Browse photos of the therapy rooms and space at Sanctuary Therapy Centre ${site_name}.`

  return (
    <>
      <Head title={pageTitle}>
        <meta name="description"         content={pageDesc} />
        <meta property="og:title"        content={pageTitle} />
        <meta property="og:description"  content={pageDesc} />
      </Head>

      <PublicLayout>
        {/* The gallery's hero: the location's copy over its photos under the
            film, or the plain blue band. Switched off, the page keeps the
            plain header it opened with before — a gallery still needs its
            title and its way back to the location. */}
        {sectionHidden(content.hero) ? (
          <section className="bg-stone pt-32 pb-14">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <a
                href={back_href}
                className="mb-6 inline-flex items-center gap-1 text-sm text-ink-muted no-underline transition-colors hover:text-ink-body"
              >
                <ChevronLeft className="h-4 w-4" />
                {back_label}
              </a>
              <h1>{heading}</h1>
              <p className="mt-3 text-ink-muted">{photoCount}</p>
            </div>
          </section>
        ) : (
          <section className="relative overflow-hidden bg-teal pt-32 pb-16 text-white">
            <HeroFilmSlides slides={heroSlides} intervalMs={hero.interval_seconds * 1000} film={hero.film} />
            <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
            <div className="relative mx-auto max-w-4xl px-6 text-center lg:px-8">
              <a
                href={back_href}
                className="mb-4 inline-flex items-center gap-1 text-sm text-white/70 no-underline transition-colors hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" />
                {back_label}
              </a>
              <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
                {content.hero.eyebrow || t.site_eyebrow}
              </p>
              {/* Plain concatenation, not cn(): tailwind-merge reads the size
                  class as a text- utility and drops text-white with it. */}
              <h1 className={`text-white ${headingSizeClass(hero.heading_size)}`.trim()}>{heading}</h1>
              {content.hero.subheading && (
                <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75">
                  {content.hero.subheading}
                </p>
              )}
              <p className="mt-4 text-sm text-white/60">{photoCount}</p>
            </div>
          </section>
        )}

        {/* Category sections */}
        {categories.length > 0 ? (
          categories.map((category) => {
            const offset = allPhotos.findIndex((p) => p.id === category.photos[0]?.id)
            return (
              <section
                key={category.key}
                id={category.key}
                className="bg-cream py-14 border-t border-hairline scroll-mt-24 first:border-t-0"
              >
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                  <h2>{category.label}</h2>
                  <div className="mt-6 columns-2 gap-4 sm:columns-3 lg:columns-4">
                    {category.photos.map((photo, i) => (
                      <div
                        key={photo.id}
                        className="group mb-4 cursor-zoom-in overflow-hidden rounded-xl break-inside-avoid"
                        onClick={() => setLightbox(offset + i)}
                      >
                        <img
                          src={photo.url}
                          alt={photo.alt_text}
                          className="w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )
          })
        ) : (
          <section className="bg-cream py-14">
            <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
              <p className="text-ink-muted">{t.no_photos}</p>
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="border-t border-hairline bg-stone py-10">
          <div className="mx-auto max-w-3xl px-6 text-center lg:px-8">
            <p className="text-ink-muted">{t.know_more}</p>
            <a
              href={back_href}
              className="mt-4 inline-block rounded-full bg-teal px-7 py-3 text-sm font-medium text-white no-underline transition-opacity hover:opacity-90"
            >
              {t.explore.replace("%{name}", site_name)}
            </a>
          </div>
        </section>
      </PublicLayout>

      <PhotoLightbox photos={allPhotos} index={lightbox} onChange={setLightbox} />
    </>
  )
}
